// src/components/customer/CustomerProfile.js
import { useState } from 'react';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { User, MapPin, Save, Loader2, Plus, Trash2 } from 'lucide-react';

export function CustomerProfile({ customer }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    contactName: customer?.contactName || '',
    phone: customer?.phone || '',
    shippingLocations: customer?.shippingLocations || []
  });

  const addLocation = () => {
    setFormData({
      ...formData,
      shippingLocations: [
        ...formData.shippingLocations,
        { name: '', street: '', city: '', state: '', zip: '', isDefault: false }
      ]
    });
  };

  const removeLocation = (index) => {
    if (formData.shippingLocations.length === 1) return;
    const updated = formData.shippingLocations.filter((_, i) => i !== index);
    if (!updated.some(loc => loc.isDefault)) {
      updated[0].isDefault = true;
    }
    setFormData({ ...formData, shippingLocations: updated });
  };

  const updateLocation = (index, field, value) => {
    const updated = [...formData.shippingLocations];
    if (field === 'isDefault' && value) {
      updated.forEach((loc, i) => loc.isDefault = i === index);
    } else {
      updated[index][field] = value;
    }
    setFormData({ ...formData, shippingLocations: updated });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'customers', customer.id), {
        contactName: formData.contactName,
        phone: formData.phone,
        shippingLocations: formData.shippingLocations.filter(loc => loc.street),
        updatedAt: Timestamp.now()
      });
      setEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Account Settings</h2>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Edit Profile
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 flex items-center gap-2"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setFormData({
                  contactName: customer?.contactName || '',
                  phone: customer?.phone || '',
                  shippingLocations: customer?.shippingLocations || []
                });
              }}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <User size={18} /> Company Information
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Company Name</label>
              <p className="font-medium">{customer?.companyName}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="font-medium">{customer?.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Contact Name</label>
              {editing ? (
                <input
                  type="text"
                  value={formData.contactName}
                  onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              ) : (
                <p className="font-medium">{customer?.contactName || '-'}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Phone</label>
              {editing ? (
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              ) : (
                <p className="font-medium">{customer?.phone || '-'}</p>
              )}
            </div>

            <div className="pt-4 border-t">
              <label className="block text-sm font-medium text-gray-500">Pricing Tier</label>
              <p className="font-medium text-green-600">{customer?.tierDiscount || 0}% discount</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Payment Method</label>
              <p className="font-medium">
                {customer?.paymentMethod === 'customerCC' && 'Credit Card'}
                {customer?.paymentMethod === 'customerInvoice' && 'Invoice (Net 30)'}
              </p>
            </div>
          </div>
        </div>

        {/* Shipping Locations */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <MapPin size={18} /> Shipping Locations
            </h3>
            {editing && (
              <button
                onClick={addLocation}
                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
              >
                <Plus size={14} /> Add Location
              </button>
            )}
          </div>

          {editing ? (
            <div className="space-y-4">
              {formData.shippingLocations.map((location, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        value={location.name}
                        onChange={(e) => updateLocation(index, 'name', e.target.value)}
                        placeholder="Location name"
                        className="px-3 py-1 border rounded text-sm"
                      />
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          checked={location.isDefault}
                          onChange={() => updateLocation(index, 'isDefault', true)}
                          className="w-4 h-4"
                        />
                        Default
                      </label>
                    </div>
                    {formData.shippingLocations.length > 1 && (
                      <button
                        onClick={() => removeLocation(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="text"
                      value={location.street}
                      onChange={(e) => updateLocation(index, 'street', e.target.value)}
                      placeholder="Street Address"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={location.city}
                        onChange={(e) => updateLocation(index, 'city', e.target.value)}
                        placeholder="City"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={location.state}
                        onChange={(e) => updateLocation(index, 'state', e.target.value)}
                        placeholder="State"
                        maxLength={2}
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={location.zip}
                        onChange={(e) => updateLocation(index, 'zip', e.target.value)}
                        placeholder="ZIP"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {customer?.shippingLocations?.length > 0 ? (
                customer.shippingLocations.map((location, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">
                      {location.name}
                      {location.isDefault && <span className="text-green-600 text-sm ml-2">(Default)</span>}
                    </p>
                    <p className="text-sm text-gray-600">{location.street}</p>
                    <p className="text-sm text-gray-600">{location.city}, {location.state} {location.zip}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-sm">No shipping locations added</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
